
/* ==========================================================================
   SHARED HARDWARE IDENTITY & LOOKUP INDEXES (/database/js/hardware-index.js)
   ========================================================================== */

const psuIndexCache = new WeakMap();

function normalizedFieldKey(value) {
  return String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

function findHardwareField(item, aliases) {
  if (!item || typeof item !== 'object') return '';
  const keys = Object.keys(item);
  for (const alias of aliases) {
    const wanted = normalizedFieldKey(alias);
    const exactKey = keys.find(key => normalizedFieldKey(key) === wanted);
    if (exactKey && item[exactKey] !== undefined && item[exactKey] !== null && item[exactKey] !== '') return item[exactKey];
  }
  for (const alias of aliases) {
    const wanted = normalizedFieldKey(alias);
    const fuzzyKey = keys.find(key => normalizedFieldKey(key).includes(wanted));
    if (fuzzyKey && item[fuzzyKey] !== undefined && item[fuzzyKey] !== null && item[fuzzyKey] !== '') return item[fuzzyKey];
  }
  return '';
}

export function normalizeHardwareIdentity(value) {
  return String(value || '')
    .normalize('NFKC')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/\([^)]*(?:mm|\bw\b)[^)]*\)/gi, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function normalizePsuFormFactor(value) {
  const raw = String(value || '').trim().toUpperCase().replace(/_/g, '-');
  if (!raw) return '';
  if (raw.includes('SFX-L') || raw.includes('SFX L') || raw === 'SFXL') return 'SFX-L';
  if (raw.includes('FLEX')) return 'FLEX';
  if (raw.includes('DC')) return 'DC';
  if (raw.includes('SFX')) return 'SFX';
  if (raw.includes('ATX')) return 'ATX';
  return '';
}

function getHardwareRecordName(item) {
  if (!item) return '';
  if (typeof item === 'string') return item;
  const brand = String(findHardwareField(item, ['brand', 'mfr', 'manufacturer', 'vendor']) || '').trim();
  const model = String(findHardwareField(item, ['model', 'name', 'product_name', 'product', 'title', 'label']) || '').trim();
  if (!model) return brand;
  return brand && !model.toLowerCase().startsWith(brand.toLowerCase()) ? `${brand} ${model}` : model;
}

function unwrapHardware(hardware) {
  return hardware?.hardware && typeof hardware.hardware === 'object' ? hardware.hardware : hardware;
}

export function getPsuCatalogIndex(hardware) {
  const source = unwrapHardware(hardware);
  if (!source || typeof source !== 'object') return null;
  if (psuIndexCache.has(source)) return psuIndexCache.get(source);

  const exact = new Map();
  const entries = [];
  for (const [groupName, list] of Object.entries(source)) {
    if (!Array.isArray(list) || !/psu|power.?supply/i.test(groupName)) continue;
    for (const item of list) {
      if (!item || typeof item !== 'object') continue;
      const normalizedName = normalizeHardwareIdentity(getHardwareRecordName(item));
      if (!normalizedName) continue;
      const formFactor = normalizePsuFormFactor(findHardwareField(item, ['form_factor', 'formfactor', 'psu_type', 'type', 'size', 'format']));
      const entry = { normalizedName, formFactor, item };
      entries.push(entry);
      if (!exact.has(normalizedName)) exact.set(normalizedName, entry);
    }
  }

  const index = { source, exact, entries };
  psuIndexCache.set(source, index);
  return index;
}

export function resolvePsuFormFactorFromCatalog(partOrName, hardware) {
  if (!partOrName) return '';
  const part = typeof partOrName === 'object' ? partOrName : { name: partOrName };
  if (part.is_custom) return '';

  const explicit = normalizePsuFormFactor(part.form_factor || part.psu_form_factor || part.type || part.format);
  if (explicit) return explicit;

  const name = String(part.name || part.model || part.title || '').trim();
  if (!name) return '';

  const target = normalizeHardwareIdentity(name);
  const slugTarget = normalizeHardwareIdentity(String(part.slug || '').replace(/[-_]+/g, ' '));
  if (!target && !slugTarget) return '';

  const index = getPsuCatalogIndex(hardware);
  if (index) {
    const direct = (target && index.exact.get(target)) || (slugTarget && index.exact.get(slugTarget));
    if (direct?.formFactor) return direct.formFactor;
  }

  let best = null;
  let bestScore = -Infinity;
  if (index) {
    for (const entry of index.entries) {
      const candidate = entry.normalizedName;
      let score = -Infinity;
      if (target && (candidate.includes(target) || target.includes(candidate))) {
        score = 5000 - Math.abs(candidate.length - target.length);
      } else if (slugTarget && (candidate.includes(slugTarget) || slugTarget.includes(candidate))) {
        score = 4500 - Math.abs(candidate.length - slugTarget.length);
      }
      if (score > bestScore) {
        bestScore = score;
        best = entry;
      }
    }
    if (best && bestScore >= 250 && best.formFactor) return best.formFactor;
  }

  // Final fallback only: submitted display strings sometimes contain the form factor.
  // Catalog identity wins first so names mentioning standards such as "ATX 3.0" do
  // not override a known SFX/SFX-L product record.
  return normalizePsuFormFactor(name);
}
